import { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { UserPlus, PencilSimple, Trash, ArrowsClockwise, ArrowRight, ClockCounterClockwise, CalendarBlank, CopySimple, Check, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useRestaurant } from "../contexts/RestaurantContext";
import { format, addDays, startOfWeek, isSameDay } from "date-fns";

const SERVER_COLORS = [
  "#2F4F4F", "#81B29A", "#E07A5F", "#F4A261", "#3D405B", "#E9C46A", "#264653", "#E76F51",
  "#6A5ACD", "#20B2AA", "#CD5C5C", "#DAA520", "#4682B4", "#9370DB", "#3CB371", "#FF6B6B",
  "#48BFE3", "#7B2D8E", "#F08080", "#2E8B57", "#DB7093", "#708090", "#B8860B", "#6B8E23",
];
const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function ServerForm({ onSubmit, buttonLabel, form, setForm }) {
  return (
    <div className="space-y-4 mt-2">
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Name</Label>
        <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Server name" className="mt-1.5" data-testid="server-name-input" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Color</Label>
        <div className="flex gap-2 flex-wrap">
          {SERVER_COLORS.map((c) => (
            <button key={c} className={`w-8 h-8 rounded-full border-2 transition-all ${form.color === c ? "ring-2 ring-offset-2" : ""}`} style={{ backgroundColor: c, borderColor: c, ringColor: c }} onClick={() => setForm({ ...form, color: c })} />
          ))}
        </div>
      </div>
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Active</Label>
        <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="server-active-switch" />
      </div>
      <Button onClick={onSubmit} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="save-server-btn">{buttonLabel}</Button>
    </div>
  );
}

export default function ServersPage() {
  const { restId } = useRestaurant();
  const [servers, setServers] = useState([]);
  const [sections, setSections] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editingServer, setEditingServer] = useState(null);
  const [form, setForm] = useState({ name: "", color: "#2F4F4F", active: true });
  const [showHandoff, setShowHandoff] = useState(false);
  const [handoffFrom, setHandoffFrom] = useState(null);
  const [handoffTo, setHandoffTo] = useState("");
  const [handoffNotes, setHandoffNotes] = useState("");
  const [handoffs, setHandoffs] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [schedules, setSchedules] = useState([]);
  const [weekSchedule, setWeekSchedule] = useState({});
  const [showCopy, setShowCopy] = useState(false);
  const [copyFromDate, setCopyFromDate] = useState(null);

  const dateStr = format(selectedDate, "yyyy-MM-dd");

  const fetchServers = useCallback(async () => {
    try {
      const { data } = await api.get("/servers");
      setServers(data);
    } catch { toast.error("Failed to load servers"); }
  }, []);

  const fetchSections = useCallback(async () => {
    try {
      const { data } = await api.get("/sections");
      setSections(data);
    } catch { /* silent */ }
  }, []);

  const fetchSchedules = useCallback(async () => {
    try {
      const params = restId ? `&restaurant_id=${restId}` : "";
      const { data } = await api.get(`/server-schedules?date=${dateStr}${params}`);
      setSchedules(data);
    } catch { /* silent */ }
  }, [dateStr, restId]);

  const fetchWeekSchedule = useCallback(async () => {
    try {
      const weekStart = format(startOfWeek(selectedDate), "yyyy-MM-dd");
      const params = restId ? `&restaurant_id=${restId}` : "";
      const { data } = await api.get(`/server-schedules/week?start_date=${weekStart}${params}`);
      setWeekSchedule(data);
    } catch { /* silent */ }
  }, [selectedDate, restId]);

  useEffect(() => { fetchServers(); fetchSections(); }, [fetchServers, fetchSections]);
  useEffect(() => { fetchSchedules(); fetchWeekSchedule(); }, [fetchSchedules, fetchWeekSchedule]);

  const createServer = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/servers", { ...form, restaurant_id: restId });
      setShowAdd(false);
      setForm({ name: "", color: "#2F4F4F", active: true });
      fetchServers();
      toast.success("Server added to staff pool");
    } catch { toast.error("Failed"); }
  };

  const updateServer = async () => {
    if (!editingServer) return;
    try {
      await api.put(`/servers/${editingServer.id}`, form);
      setShowEdit(false);
      setEditingServer(null);
      fetchServers();
      toast.success("Server updated");
    } catch { toast.error("Failed"); }
  };

  const deleteServer = async (id) => {
    try {
      await api.delete(`/servers/${id}`);
      fetchServers();
      fetchSchedules();
      toast.success("Server removed");
    } catch { toast.error("Failed"); }
  };

  const openEdit = (s) => {
    setEditingServer(s);
    setForm({ name: s.name, color: s.color, active: s.active });
    setShowEdit(true);
  };

  const getServerSections = (serverId) => sections.filter((s) => s.server_id === serverId);

  // ─── Schedule Management ───
  const isScheduled = (serverId) => schedules.some((s) => s.server_id === serverId);

  const toggleSchedule = async (server) => {
    const existing = schedules.find((s) => s.server_id === server.id);
    if (existing) {
      try {
        await api.delete(`/server-schedules/${existing.id}`);
        fetchSchedules();
        fetchWeekSchedule();
        toast.success(`${server.name} removed from ${format(selectedDate, "MMM d")}`);
      } catch { toast.error("Failed"); }
    } else {
      try {
        await api.post("/server-schedules", { server_id: server.id, date: dateStr, restaurant_id: restId });
        fetchSchedules();
        fetchWeekSchedule();
        toast.success(`${server.name} added to ${format(selectedDate, "MMM d")}`);
      } catch { toast.error("Failed"); }
    }
  };

  const copySchedule = async () => {
    if (!copyFromDate) return;
    try {
      const fromStr = format(copyFromDate, "yyyy-MM-dd");
      const { data } = await api.post("/server-schedules/copy", { from_date: fromStr, to_date: dateStr, restaurant_id: restId });
      toast.success(`Copied ${data.copied} server(s) from ${format(copyFromDate, "MMM d")}`);
      setShowCopy(false);
      fetchSchedules();
      fetchWeekSchedule();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to copy"); }
  };

  // ─── Rotation & Handoff ───
  const [rotationStatus, setRotationStatus] = useState(null);

  const fetchRotationStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/servers/rotation-status");
      setRotationStatus(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchRotationStatus(); }, [fetchRotationStatus]);

  const runRotation = async () => {
    try {
      const { data } = await api.post("/servers/rotate");
      toast.success(`Rotation complete: ${data.assignments_made} assignments updated`);
      fetchServers();
      fetchSections();
      fetchRotationStatus();
    } catch { toast.error("Rotation failed"); }
  };

  const fetchHandoffs = useCallback(async () => {
    try {
      const { data } = await api.get("/servers/handoffs");
      setHandoffs(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchHandoffs(); }, [fetchHandoffs]);

  const openHandoff = (srv) => {
    setHandoffFrom(srv);
    setHandoffTo("");
    setHandoffNotes("");
    setShowHandoff(true);
  };

  const executeHandoff = async () => {
    if (!handoffFrom || !handoffTo) return;
    try {
      const { data } = await api.post("/servers/handoff", {
        from_server_id: handoffFrom.id,
        to_server_id: handoffTo,
        notes: handoffNotes,
      });
      toast.success(`Handed off ${data.sections_count} section(s) to ${data.to_server_name}`);
      setShowHandoff(false);
      fetchServers();
      fetchSections();
      fetchRotationStatus();
      fetchHandoffs();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Handoff failed");
    }
  };

  const scheduledServerIds = new Set(schedules.map((s) => s.server_id));
  const todaysTeam = servers.filter((s) => scheduledServerIds.has(s.id));
  const staffPool = servers.filter((s) => !scheduledServerIds.has(s.id));

  // Week overview
  const weekStart = startOfWeek(selectedDate);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="servers-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 sm:mb-6 gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold tracking-tight" style={{ fontFamily: "Work Sans", color: "#1C1917" }}>Servers</h2>
          <p className="text-xs sm:text-sm text-stone-500 mt-1">{format(selectedDate, "EEEE, MMMM d, yyyy")} &middot; {todaysTeam.length} scheduled</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs sm:text-sm" data-testid="server-date-picker">
                <CalendarBlank size={16} className="mr-1" /> {format(selectedDate, "MMM d")}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar mode="single" selected={selectedDate} onSelect={(d) => d && setSelectedDate(d)} data-testid="server-calendar" />
            </PopoverContent>
          </Popover>
          <Button variant="outline" size="sm" onClick={() => setShowCopy(true)} className="text-xs sm:text-sm" data-testid="copy-schedule-btn">
            <CopySimple size={16} className="mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={runRotation} data-testid="auto-rotate-btn" className="text-xs sm:text-sm">
            <ArrowsClockwise size={16} className="mr-1" /> Rotate
          </Button>
          <Button onClick={() => { setForm({ name: "", color: "#2F4F4F", active: true }); setShowAdd(true); }} style={{ backgroundColor: "#2F4F4F" }} size="sm" data-testid="add-server-btn" className="text-xs sm:text-sm">
            <UserPlus size={16} className="mr-1" /> Add
          </Button>
        </div>
      </div>

      {/* Week Overview Strip */}
      <div className="bg-white rounded-lg border border-stone-200 p-3 mb-5" data-testid="week-overview">
        <div className="flex items-center justify-between mb-2">
          <button onClick={() => setSelectedDate(addDays(selectedDate, -7))} className="p-1 rounded hover:bg-stone-100 text-stone-500"><CaretLeft size={16} /></button>
          <span className="text-xs font-semibold uppercase tracking-widest text-stone-500">Week of {format(weekStart, "MMM d")}</span>
          <button onClick={() => setSelectedDate(addDays(selectedDate, 7))} className="p-1 rounded hover:bg-stone-100 text-stone-500"><CaretRight size={16} /></button>
        </div>
        <div className="grid grid-cols-7 gap-1">
          {weekDays.map((day) => {
            const dayStr = format(day, "yyyy-MM-dd");
            const daySchedules = weekSchedule[dayStr] || [];
            const isToday = isSameDay(day, new Date());
            const isSelected = isSameDay(day, selectedDate);
            return (
              <button
                key={dayStr}
                onClick={() => setSelectedDate(day)}
                className={`p-2 rounded-lg text-center transition-all ${isSelected ? "ring-2 ring-[#2F4F4F] bg-[#2F4F4F]/5" : "hover:bg-stone-50"} ${isToday ? "border border-[#2F4F4F]" : "border border-transparent"}`}
                data-testid={`week-day-${dayStr}`}
              >
                <p className="text-[10px] uppercase tracking-wider text-stone-500 font-semibold">{DAY_NAMES[day.getDay()]}</p>
                <p className={`text-sm font-bold ${isSelected ? "text-[#2F4F4F]" : "text-stone-700"}`}>{format(day, "d")}</p>
                <div className="flex justify-center gap-0.5 mt-1 min-h-[10px]">
                  {daySchedules.slice(0, 4).map((s) => {
                    const srv = servers.find((sv) => sv.id === s.server_id);
                    return <div key={s.id} className="w-2 h-2 rounded-full" style={{ backgroundColor: srv?.color || "#9CA3AF" }} />;
                  })}
                  {daySchedules.length > 4 && <span className="text-[8px] text-stone-500">+{daySchedules.length - 4}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Today's Team */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3" data-testid="todays-team-label">
          {format(selectedDate, "MMM d")} Team ({todaysTeam.length})
        </h3>
        {todaysTeam.length === 0 ? (
          <div className="bg-white rounded-lg border border-dashed border-stone-300 p-6 text-center">
            <p className="text-sm text-stone-500">No servers scheduled for this day</p>
            <p className="text-xs text-stone-300 mt-1">Click servers below to add them, or copy from another day</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {todaysTeam.map((s, idx) => {
              const srvSections = getServerSections(s.id);
              return (
                <div key={s.id} className="bg-white rounded-lg border border-stone-200 p-3 animate-fade-in" style={{ animationDelay: `${idx * 40}ms` }} data-testid={`team-card-${s.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm" style={{ backgroundColor: s.color }}>
                      {s.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-800 text-sm">{s.name}</span>
                        <Badge className="bg-green-100 text-green-700 text-[10px]">Scheduled</Badge>
                      </div>
                      {srvSections.length > 0 && (
                        <div className="flex items-center gap-1 mt-0.5">
                          {srvSections.map((sec) => (
                            <span key={sec.id} className="text-[9px] px-1 py-0.5 rounded" style={{ backgroundColor: sec.color + "20", color: sec.color }}>{sec.name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(s)} title="Edit" data-testid={`edit-server-${s.id}`} className="text-stone-500 hover:text-stone-700 h-8 w-8 p-0">
                        <PencilSimple size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openHandoff(s)} title="Handoff" data-testid={`handoff-server-${s.id}`} className="text-stone-500 hover:text-[#2F4F4F] h-8 w-8 p-0">
                        <ArrowRight size={14} />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => toggleSchedule(s)} title="Remove from day" data-testid={`unschedule-${s.id}`} className="text-red-400 hover:text-red-600 h-8 w-8 p-0">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Staff Pool */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500 mb-3" data-testid="staff-pool-label">
          Staff Pool ({staffPool.length} available)
        </h3>
        {servers.length === 0 ? (
          <div className="text-center py-8 animate-fade-in">
            <p className="text-stone-500 text-sm">No servers in staff pool yet</p>
          </div>
        ) : staffPool.length === 0 ? (
          <p className="text-xs text-stone-500 text-center py-4">All servers are scheduled for this day</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
            {staffPool.map((s) => (
              <div
                key={s.id}
                className="flex items-center gap-2.5 p-3 rounded-lg border border-stone-200 bg-white hover:border-stone-300 transition-all text-left"
                data-testid={`schedule-server-${s.id}`}
              >
                <button onClick={() => toggleSchedule(s)} className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 hover:opacity-80 transition-opacity" style={{ backgroundColor: s.color }}>
                  {s.name.charAt(0).toUpperCase()}
                </button>
                <div className="flex-1 min-w-0" onClick={() => toggleSchedule(s)} style={{ cursor: "pointer" }}>
                  <p className="text-sm font-medium text-stone-700 truncate">{s.name}</p>
                  <p className="text-[10px] text-stone-500">Click to schedule</p>
                </div>
                <Button variant="ghost" size="sm" onClick={() => openEdit(s)} className="h-7 w-7 p-0 text-stone-300 hover:text-stone-600" data-testid={`edit-pool-server-${s.id}`}>
                  <PencilSimple size={12} />
                </Button>
              </div>
            ))}
          </div>
        )}
        {/* Manage staff link */}
        <div className="flex items-center gap-3 mt-3">
          {servers.map((s) => (
            <div key={s.id} className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
              <span className="text-[10px] text-stone-500">{s.name}</span>
              <button onClick={() => openEdit(s)} className="text-stone-300 hover:text-stone-500"><PencilSimple size={10} /></button>
              <button onClick={() => deleteServer(s.id)} className="text-stone-300 hover:text-red-500"><Trash size={10} /></button>
            </div>
          ))}
        </div>
      </div>

      {/* Handoff History */}
      {handoffs.length > 0 && (
        <div className="bg-white rounded-lg border border-stone-200 p-4 sm:p-5" data-testid="handoff-history">
          <div className="flex items-center gap-2 mb-3">
            <ClockCounterClockwise size={16} weight="duotone" style={{ color: "#2F4F4F" }} />
            <h3 className="text-sm font-semibold uppercase tracking-widest text-stone-500">Handoff History</h3>
          </div>
          <div className="space-y-1.5 max-h-[180px] overflow-y-auto">
            {handoffs.slice(0, 10).map((h) => (
              <div key={h.id} className="flex items-center gap-2 p-2 rounded-lg border border-stone-100 text-xs" data-testid={`handoff-item-${h.id}`}>
                <span className="font-medium text-stone-700">{h.from_server_name}</span>
                <ArrowRight size={12} className="text-stone-300" />
                <span className="font-medium text-stone-700">{h.to_server_name}</span>
                <span className="text-stone-500 ml-auto">{h.sections_count} sec</span>
                <span className="text-[10px] text-stone-300">
                  {new Date(h.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── Dialogs ─── */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-server-dialog">
          <DialogHeader><DialogTitle>Add Server to Staff Pool</DialogTitle></DialogHeader>
          <ServerForm onSubmit={createServer} buttonLabel="Add Server" form={form} setForm={setForm} />
        </DialogContent>
      </Dialog>

      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent data-testid="edit-server-dialog">
          <DialogHeader><DialogTitle>Edit Server</DialogTitle></DialogHeader>
          <ServerForm onSubmit={updateServer} buttonLabel="Save Changes" form={form} setForm={setForm} />
        </DialogContent>
      </Dialog>

      <Dialog open={showHandoff} onOpenChange={setShowHandoff}>
        <DialogContent data-testid="handoff-dialog">
          <DialogHeader><DialogTitle>Shift Handoff</DialogTitle></DialogHeader>
          {handoffFrom && (
            <div className="space-y-4 mt-2">
              <div className="p-3 rounded-lg bg-stone-50 border border-stone-200">
                <p className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-1">Outgoing</p>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: handoffFrom.color }}>{handoffFrom.name.charAt(0)}</div>
                  <span className="font-medium text-stone-800">{handoffFrom.name}</span>
                </div>
              </div>
              <div className="flex justify-center"><ArrowRight size={20} className="text-stone-300" /></div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Incoming Server</Label>
                <Select value={handoffTo || "none"} onValueChange={(v) => setHandoffTo(v === "none" ? "" : v)}>
                  <SelectTrigger className="mt-1.5" data-testid="handoff-to-select"><SelectValue placeholder="Select server" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" disabled>Select server</SelectItem>
                    {servers.filter((s) => s.id !== handoffFrom.id && s.active).map((s) => (
                      <SelectItem key={s.id} value={s.id}><span className="flex items-center gap-2"><span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />{s.name}</span></SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold">Notes</Label>
                <Input value={handoffNotes} onChange={(e) => setHandoffNotes(e.target.value)} placeholder="e.g. Table 5 needs refill" className="mt-1.5" data-testid="handoff-notes-input" />
              </div>
              <Button onClick={executeHandoff} disabled={!handoffTo} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="execute-handoff-btn">Complete Handoff</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Copy Schedule Dialog */}
      <Dialog open={showCopy} onOpenChange={setShowCopy}>
        <DialogContent data-testid="copy-schedule-dialog">
          <DialogHeader><DialogTitle>Copy Schedule</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-xs text-stone-500">Copy a server schedule from another day to <span className="font-semibold text-stone-700">{format(selectedDate, "EEEE, MMM d")}</span>.</p>
            <div>
              <Label className="text-xs uppercase tracking-widest text-stone-500 font-semibold mb-2 block">Copy From</Label>
              <Calendar mode="single" selected={copyFromDate} onSelect={setCopyFromDate} data-testid="copy-from-calendar" />
            </div>
            {copyFromDate && (
              <p className="text-xs text-stone-500">
                Will copy schedule from <span className="font-semibold">{format(copyFromDate, "EEEE, MMM d")}</span> and replace {format(selectedDate, "MMM d")}'s schedule.
              </p>
            )}
            <Button onClick={copySchedule} disabled={!copyFromDate} style={{ backgroundColor: "#2F4F4F" }} className="w-full" data-testid="confirm-copy-btn">
              <CopySimple size={16} className="mr-1.5" /> Copy Schedule
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
